import { RefObject, useEffect, useRef } from 'react';
import { isWithinTapSlop, TapOrigin, tapOrigin } from 'utils/tapGesture';

/**
 * Dismiss a floating surface when the player taps outside it.
 *
 * The table already had three hand-rolled versions of this idea — TableSwitcher
 * (`mousedown`), RaiseAction (`pointerdown`) and the avatar/notification
 * dropdowns (MUI) — none of which agreed on the event or handled touch. This is
 * the shared one; prefer it over a fourth copy.
 *
 * WHAT MAKES IT SAFE ON TOUCH
 * ---------------------------
 * Dismissal needs the WHOLE gesture to belong outside the surface: pointer down
 * outside, pointer up outside, and less than a tap's worth of movement in
 * between (see utils/tapGesture). That is the same rule GameModal's backdrop
 * uses, and it is what stops three accidents — a drag that starts inside and is
 * released outside (selecting chat text, dragging the bet slider), a scroll or
 * table-swipe that merely begins out on the felt, and a fling whose release
 * lands somewhere unrelated.
 *
 * THE TOGGLE-BUTTON TRAP
 * ----------------------
 * The launchers that open these panels sit OUTSIDE them (chat and history
 * icons in the footer bar) and are `setIsOpen(prev => !prev)`. Our pointerup
 * runs BEFORE the button's click, so dismissing on a tap of the launcher would
 * close the panel and let the button immediately re-open it. Tag the launcher
 * with `panelToggleProps(name)` and pass the same `toggleName` here; taps on it
 * are then left entirely to the button, which closes the panel exactly once.
 *
 * SWALLOWING A DESTRUCTIVE TAP
 * ----------------------------
 * The poker action bar is also outside these panels and is NOT suppressed while
 * one is open, so the tap that dismisses a panel could land on FOLD and commit
 * an irreversible real-money action in the same gesture. `swallowTapWithin`
 * names the regions where the dismissing tap must be consumed: the panel closes
 * and the player taps again deliberately. See the ticket's open question — this
 * is a product call.
 */

/** Marks a control whose own click already toggles the surface. */
export const PANEL_TOGGLE_ATTR = 'data-panel-toggle';

/** Spread onto the control that opens/closes the surface. */
export const panelToggleProps = (name: string) => ({ [PANEL_TOGGLE_ATTR]: name });

/** Names shared by the two table panels and their footer launchers. */
export const CHAT_TOGGLE = 'chat';
export const HISTORY_TOGGLE = 'history';

/**
 * The poker action bar. A tap here while a panel is open closes the panel and
 * is then consumed, so the same gesture cannot also fire FOLD / CALL.
 */
export const ACTION_BAR_SELECTOR = '.poker-actions';

/**
 * One table's view container — the grid cell in multi-table mode, the page
 * shell in single-table mode (both tagged in pages/Game.tsx). Also what
 * GameModal, WinChips and useTrayClearanceLift scope themselves by.
 */
export const TABLE_VIEW_ROOT_SELECTOR = '[data-table-view-root]';

type UseOutsideTapDismissOptions = {
  /**
   * Only listen while the surface is really on screen. Chat and TableHistory
   * stay MOUNTED when closed (they are hidden with `visibility`), so an
   * unguarded listener would keep firing for a panel nobody can see.
   */
  enabled: boolean;
  /** The surface itself. Taps anywhere inside it are ignored. */
  surfaceRef: RefObject<HTMLElement | null>;
  onDismiss: () => void;
  /** Matches the `panelToggleProps(name)` on this surface's launcher. */
  toggleName?: string;
  /**
   * CSS selector for regions where the dismissing tap must NOT also activate
   * whatever it hit (the poker action bar). Only the tap that actually closes
   * the surface is consumed.
   */
  swallowTapWithin?: string;
};

/**
 * Consume the `click` that the browser will synthesise for the gesture we just
 * used to dismiss, so it cannot reach FOLD/CALL underneath.
 *
 * Only a click landing back inside `withinSelector` — the region the dismissing
 * pointerup was in — belongs to that gesture. Swallowing whatever click happens
 * to arrive next would be an intermittent lost-action bug instead: touch paths
 * do not always follow a pointerup with a click, and the listener would then be
 * sitting armed when the player deliberately taps something else. Anything else
 * passes through untouched and merely retires the listener; so does the timeout,
 * for a gesture that produces no click at all.
 */
export const swallowNextClick = (withinSelector: string) => {
  let timeoutId = 0;

  const consume = (event: MouseEvent) => {
    if (event.target instanceof Element && event.target.closest(withinSelector)) {
      event.stopPropagation();
      event.preventDefault();
    }
    cleanup();
  };

  const cleanup = () => {
    window.clearTimeout(timeoutId);
    document.removeEventListener('click', consume, true);
  };

  document.addEventListener('click', consume, true);
  timeoutId = window.setTimeout(cleanup, 400);
};

export const useOutsideTapDismiss = ({
  enabled,
  surfaceRef,
  onDismiss,
  toggleName,
  swallowTapWithin
}: UseOutsideTapDismissOptions): void => {
  // Held in a ref so a new inline callback on every render does not tear the
  // document listeners down and rebuild them mid-gesture.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!enabled) return undefined;

    // The gesture belongs to ONE pointer from down to up. Keeping only an
    // origin would let a second finger's release be measured against the first
    // finger's origin, so a two-finger gesture out on the felt could dismiss a
    // panel nobody tapped outside of.
    let gesture: { pointerId: number; origin: TapOrigin } | null = null;

    const isInside = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false;
      if (surfaceRef.current?.contains(target)) return true;
      if (!toggleName || !(target instanceof Element)) return false;
      return !!target.closest(`[${PANEL_TOGGLE_ATTR}="${toggleName}"]`);
    };

    /**
     * A tap belongs to the table cell it landed in.
     *
     * The multi-table grid mounts a whole table view per cell, each with its own
     * Chat and Table History and its own open flag (MultiTableGrid wraps every
     * cell in its own TexasChatContextProvider, and useTableScopedOpen passes
     * `open` straight through in grid mode). So several of these hooks can be
     * enabled at once, and they all listen on `document` — a tap on table A's
     * felt is "outside" table B's panel too, and used to close every open panel
     * on the page.
     *
     * Only OTHER CELLS are excluded, not everything beyond our own: chrome that
     * sits outside every cell root (the shared header portals out of the grid)
     * is global, and a tap there still dismisses. Single-table mode and the
     * lobby have one cell root or none, so nothing changes there.
     */
    const isInAnotherTableView = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      const ownRoot = surfaceRef.current?.closest(TABLE_VIEW_ROOT_SELECTOR);
      if (!ownRoot) return false;
      const targetRoot = target.closest(TABLE_VIEW_ROOT_SELECTOR);
      return !!targetRoot && targetRoot !== ownRoot;
    };

    const handlePointerDown = (event: PointerEvent) => {
      // A second finger makes this multi-touch, not a tap: abandon the gesture
      // in progress rather than letting either finger's release dismiss.
      if (event.isPrimary === false) {
        gesture = null;
        return;
      }

      gesture =
        isInside(event.target) || isInAnotherTableView(event.target)
          ? null
          : { pointerId: event.pointerId, origin: tapOrigin(event) };
    };

    const handlePointerUp = (event: PointerEvent) => {
      const start = gesture;
      // Only the pointer that began the gesture can complete it; another
      // pointer's release leaves it pending.
      if (!start || start.pointerId !== event.pointerId) return;
      gesture = null;

      if (isInside(event.target)) return;
      // Released over another table's cell — a drag across the grid, or that
      // table's business. Not a tap outside THIS panel.
      if (isInAnotherTableView(event.target)) return;
      // Secondary / middle buttons open menus, they are not a dismiss gesture.
      if (event.button > 0) return;
      // Moved too far: a scroll, a swipe or a drag — not a tap.
      if (!isWithinTapSlop(start.origin, event)) return;

      if (
        swallowTapWithin &&
        event.target instanceof Element &&
        event.target.closest(swallowTapWithin)
      ) {
        swallowNextClick(swallowTapWithin);
      }

      onDismissRef.current();
    };

    // Capture phase: the felt and the action rail stop propagation in places,
    // and this must still see the gesture. We only observe, never cancel.
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointerup', handlePointerUp, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
    };
  }, [enabled, surfaceRef, toggleName, swallowTapWithin]);
};
